// src/components/Layout/Sidebar.js
import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FaBars,
  FaChartBar,
  FaChevronLeft,
  FaCog,
  FaEnvelopeOpen,
  FaInbox,
  FaHome,
  FaRss,
  FaSearch,
  FaSearchengin,
  FaShieldAlt,
  FaSync,
} from "react-icons/fa";
import "./Sidebar.css";
import { useModuleSettings } from "../../context/ModuleSettingsContext";
import { canAccessSettings } from "../../constants/roles";
import useSystemHealth from "../../hooks/useSystemHealth";

// Maps the health hook's overall state to what the status pill renders.
const STATUS_PRESENTATION = {
  checking: { cls: "is-checking", line: "Checking systems…", title: "Checking system status" },
  online: { cls: "is-online", line: "All systems online", title: "Frontend, API and database connected" },
  degraded: { cls: "is-degraded", line: "Database unreachable", title: "API is up but the database is not responding" },
  offline: { cls: "is-offline", line: "Backend unreachable", title: "Cannot reach the API server" },
};

function getStoredUser() {
  const raw = localStorage.getItem("auth_user") || sessionStorage.getItem("auth_user");
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

// Nav items are grouped into labelled sections for an enterprise feel.
// Items keyed with `moduleKey` are gated by the admin module-settings toggle;
// items without one (like Home) always show. A whole section is hidden when
// none of its items are enabled.
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: ".", end: true, icon: <FaHome />, label: "Home" },
      { to: "dashboard", moduleKey: "dashboard", icon: <FaChartBar />, label: "Dashboard" },
    ],
  },
  {
    label: "Email Analysis",
    items: [
      { to: "phishing-ai", moduleKey: "phishing-ai", icon: <FaEnvelopeOpen />, label: "AI Phishing Analysis" },
      { to: "phishing", moduleKey: "phishing", icon: <FaEnvelopeOpen />, label: "Phishing Analysis" },
      { to: "reported-emails", moduleKey: "defender-submissions", icon: <FaInbox />, label: "User Submissions" },
    ],
  },
  {
    label: "Threat Intelligence",
    items: [
      { to: "threat-intel", moduleKey: "threat-intel", icon: <FaRss />, label: "Threat Intelligence" },
      { to: "ioc", moduleKey: "ioc", icon: <FaSearch />, label: "Unified IoC Validator" },
      { to: "query-generator", moduleKey: "query-generator", icon: <FaSearchengin />, label: "Threat Hunt Query Generator" },
    ],
  },
  {
    label: "Exposure",
    items: [
      { to: "vulnerabilities", moduleKey: "vulnerabilities", icon: <FaShieldAlt />, label: "Vulnerability Center", beta: true },
    ],
  },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const showSettings = useMemo(() => {
    const user = getStoredUser();
    return canAccessSettings(user?.role);
  }, []);
  const { isEnabled } = useModuleSettings();
  const { overall: healthStatus, isChecking, refresh } = useSystemHealth();
  const status = STATUS_PRESENTATION[healthStatus] || STATUS_PRESENTATION.checking;

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // auto-collapse on smaller screens
      if (mobile) {
        setCollapsed(true);
      }
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  // Resolve module gating once, then drop any section left with no items.
  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.moduleKey || isEnabled(item.moduleKey)),
      })).filter((group) => group.items.length > 0),
    [isEnabled]
  );

  const renderItem = ({ to, icon, label, end = false, beta = false }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) => `item ${isActive ? "active" : ""}`}
      data-tooltip={`${label}${beta ? " · Beta" : ""}`}
    >
      <span className="item-rail" aria-hidden />
      <span className="icon" aria-hidden>
        {icon}
      </span>
      <span className="label">
        {label}
        {beta && <span className="beta-badge beta-badge-sm">Beta</span>}
      </span>
    </NavLink>
  );

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${isMobile ? "mobile" : ""}`}>
      <div className="sidebar-top">
        {!collapsed && <span className="sidebar-top-label">Security Console</span>}
        <button
          type="button"
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <FaBars /> : <FaChevronLeft />}
        </button>
      </div>

      <nav className="menu" aria-label="Primary">
        {visibleGroups.map((group) => (
          <div className="nav-group" key={group.label}>
            <span className="nav-group-label">{group.label}</span>
            {group.items.map(renderItem)}
          </div>
        ))}
      </nav>

      <div className="sidebar-bottom">
        {showSettings && (
          <NavLink
            to="admin/settings"
            className={({ isActive }) => `item ${isActive ? "active" : ""}`}
            data-tooltip="Settings"
          >
            <span className="item-rail" aria-hidden />
            <span className="icon" aria-hidden><FaCog /></span>
            <span className="label">Settings</span>
          </NavLink>
        )}

        <div className={`sidebar-status ${status.cls}`} title={status.title}>
          <span className="sidebar-status-dot" aria-hidden />
          <span className="sidebar-status-text">
            <span className="sidebar-status-line">{status.line}</span>
          </span>
          <button
            type="button"
            className={`sidebar-status-refresh ${isChecking ? "spinning" : ""}`}
            onClick={refresh}
            disabled={isChecking}
            title="Refresh status"
            aria-label="Refresh system status"
          >
            <FaSync />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
